import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateInstructionVersion } from '@partful/entities/instructions/version';
import { VersionModel } from '@partful/instructions-domain-models';
import { classNames } from '@partful/shared/lib/classNames';
import { Autocomplete } from '@partful/shared/ui/Autocomplete';
import { Textarea } from '@partful/shared/ui/Textarea';
import { Typography } from '@partful/shared/ui/Typography';
import { ComponentProps, forwardRef, useEffect } from 'react';
import { Controller, FormState, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './OverviewForm.module.scss';

type OverviewFormProps = {
  version: VersionModel;
  onSubmit: SubmitHandler<UpdateInstructionVersion>;
  onStateChange?: (state: FormState<UpdateInstructionVersion>) => void;
  disabled?: boolean;
} & Omit<ComponentProps<'form'>, 'onSubmit'>;

export const OverviewForm = forwardRef<HTMLFormElement, OverviewFormProps>(
  ({ version, onSubmit, onStateChange, disabled, className, ...rest }, ref) => {
    const { t: i18n } = useTranslation();

    const { formState, handleSubmit, control } = useForm<UpdateInstructionVersion>({
      resolver: zodResolver(UpdateInstructionVersion),
      mode: 'onChange',
      values: {
        description: version.description,
      },
      disabled,
    });

    useEffect(() => {
      onStateChange?.(formState);
    }, [formState, onStateChange]);

    return (
      <form className={classNames(styles.form, className)} {...rest} onSubmit={handleSubmit(onSubmit)} ref={ref}>
        <div className={classNames(styles.control, styles.authorControl)}>
          <Typography as="label" htmlFor="authorName" fontWeight={600}>
            {i18n('instructions:version.data.authorName.label')}
          </Typography>
          <Autocomplete
            id="authorName"
            disabled
            size="medium"
            defaultValue={version.authorName}
            freeSolo
            options={[]}
          />
        </div>
        <div className={styles.control}>
          <Typography as="label" htmlFor="description" fontWeight={600}>
            {i18n('instructions:version.data.description.label')} <sup>({i18n('instructions:common.optional')})</sup>
          </Typography>
          <Controller
            name="description"
            control={control}
            render={({ field: { name, onBlur, onChange, value, disabled }, fieldState: { error } }) => (
              <>
                <Textarea
                  id={name}
                  name={name}
                  placeholder={i18n('instructions:version.data.description.placeholder')}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  disabled={disabled}
                  minRows={3}
                />
                {error && (
                  <Typography appearance="label" className={styles.errorMessage}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                    {i18n(
                      /* istanbul ignore next - very defensive fallback case */ error.message ??
                        'instructions:version.data.description.validation.generic'
                    )}
                  </Typography>
                )}
              </>
            )}
          />
        </div>
      </form>
    );
  }
);
